import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import endpoints from '../api/endpoints';
import LoadingSpinner from '../components/LoadingSpinner';
import { MessageSquare, Play, Trash2, Calendar, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import '../assets/css/conversationHistory.css';

const ConversationHistory = () => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState(null);

  const { data: threadsData, isLoading, refetch } = useQuery(
    'user-conversation-history',
    () => api.get(endpoints.conversations.list).then(res => res.data),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const handleContinue = (threadId) => {
    // Navigate to query interface with threadId in state
    navigate('/query', { state: { continueThreadId: threadId } });
  };

  const handleDelete = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? You can still view it in your history but won\'t be able to continue it.')) {
      return;
    }

    setDeletingId(threadId);
    try {
      await api.delete(endpoints.conversations.softDelete(threadId));
      toast.success('Conversation deleted');
      refetch();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading your conversations..." />;
  }

  const threads = threadsData?.threads || [];

  return (
    <div className="conversation-history-container">
      <div className="history-header">
        <div>
          <h1 className="history-title">Conversation History</h1>
          <p className="history-subtitle">
            View and continue your past conversations with Nick
          </p>
        </div>
        <div className="history-stats">
          <div className="stat-item">
            <MessageSquare size={20} />
            <span>{threads.length} conversation{threads.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {threads.length === 0 ? (
        <div className="empty-history">
          <MessageCircle size={64} className="empty-icon" />
          <h2 className="empty-title">No conversations yet</h2>
          <p className="empty-text">
            Start a conversation with Nick to see your history here
          </p>
          <button 
            className="action-button primary"
            onClick={() => navigate('/query')}
          >
            Ask Nick a Question
          </button>
        </div>
      ) : (
        <div className="conversations-list">
          {threads.map((thread) => (
            <div key={thread._id} className="conversation-card">
              <div className="conversation-content">
                <div className="conversation-header">
                  <div className="conversation-meta">
                    <Calendar size={14} />
                    <span className="conversation-date">
                      {formatDate(thread.lastActivity)}
                    </span>
                    <span className="conversation-time">
                      {formatTime(thread.lastActivity)}
                    </span>
                  </div>
                  <div className="conversation-badge">
                    {thread.messagesCount} message{thread.messagesCount !== 1 ? 's' : ''}
                  </div>
                </div>

                <h3 className="conversation-title">
                  {thread.metadata?.firstQuery || 'Untitled Conversation'}
                </h3>

                {thread.metadata?.lastQuery && thread.metadata.lastQuery !== thread.metadata.firstQuery && (
                  <p className="conversation-preview">
                    Last: {thread.metadata.lastQuery.substring(0, 100)}
                    {thread.metadata.lastQuery.length > 100 ? '...' : ''}
                  </p>
                )}

                <div className="conversation-footer">
                  <div className="conversation-info">
                    <span className="info-item">
                      Started {formatDate(thread.threadStarted)}
                    </span>
                    {thread.metadata?.totalTokensUsed > 0 && (
                      <span className="info-item">
                        {thread.metadata.totalTokensUsed.toLocaleString()} tokens
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="conversation-actions">
                <button
                  className="action-btn continue-btn"
                  onClick={() => handleContinue(thread._id)}
                  title="Continue this conversation"
                >
                  <Play size={16} />
                  Continue
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDelete(thread._id)}
                  disabled={deletingId === thread._id}
                  title="Delete this conversation"
                >
                  <Trash2 size={16} />
                  {deletingId === thread._id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationHistory;
